// One lesson as an ItemCard: order number (or a course icon when shown
// outside its course) in the tile, title, summary, and the chip row. Used by
// the course page list, the home page's "New since you joined" strip and
// search results, and the lesson page's "Up next" cards.
import { useNavigate } from "react-router-dom";
import ItemCard from "../../components/common/ItemCard";
import { useAuth } from "../../context/AuthContext";
import { getCourse } from "../catalog";
import { LessonDefinition } from "../catalog/types";
import { useAcademyProgress } from "../progress";
import { DoneChip, GateChip, MinutesChip, NewChip, PlatformChip, WalkthroughChip, WatchChip } from "./Chip";
import { CourseIcon } from "./courseIcons";
import { useGateStatus } from "./gates";

export const lessonPath = (l: LessonDefinition) => `/academy/${l.courseId}/${l.id}`;

/** New = released after the officer joined AND not opened yet. */
export function isNewForUser(lesson: LessonDefinition, createdAt: string | Date | null | undefined, viewed = false): boolean {
  if (viewed) return false;
  const joined = createdAt ? new Date(createdAt).getTime() : NaN;
  return Number.isFinite(joined) && Date.parse(lesson.releasedAt) > joined;
}

const LessonCard: React.FC<{
  lesson: LessonDefinition;
  /** Shows the course icon instead of the order number (for cross-course lists). */
  showCourse?: boolean;
  /** Small label above the title ("Previous" / "Up next"). */
  eyebrow?: string;
  className?: string;
}> = ({ lesson, showCourse, eyebrow, className }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isCompleted, isViewed } = useAcademyProgress();
  const gate = useGateStatus(lesson.gate);
  const course = getCourse(lesson.courseId);
  const isNew = isNewForUser(lesson, (user as any)?.created_at, isViewed(lesson.id));

  return (
    <ItemCard
      className={className}
      onClick={() => navigate(lessonPath(lesson))}
      icon={
        showCourse && course ? (
          <CourseIcon icon={course.icon} />
        ) : (
          <span className="h-6 w-6 flex items-center justify-center text-primary font-semibold text-sm tabular-nums">{lesson.order}</span>
        )
      }
      title={
        <span className="inline-flex flex-col">
          {eyebrow && <span className="text-[10px] font-semibold uppercase tracking-wider text-base-content/50">{eyebrow}</span>}
          <span>{lesson.title}</span>
          {lesson.subtitle && <span className="text-xs font-normal text-base-content/50">{lesson.subtitle}</span>}
        </span>
      }
      actions={
        <>
          {isNew && <NewChip />}
          {isCompleted(lesson.id) && <DoneChip />}
        </>
      }
      subtitle={lesson.summary}
      meta={
        <>
          <MinutesChip minutes={lesson.minutes} />
          {showCourse && course && <span className="text-xs text-base-content/50 truncate">{course.title}</span>}
          {lesson.tourId && <WalkthroughChip />}
          {lesson.sceneId && <WatchChip />}
          {lesson.platform && <PlatformChip platform={lesson.platform} />}
          {gate && <GateChip label={gate.label} locked={gate.locked} reason={gate.reason} />}
        </>
      }
    />
  );
};

export default LessonCard;
